import { useMutation, useQuery } from '@tanstack/react-query';
import { getPrivateAxiosInstance } from '../../../axiosInstances/axiosPrivate';
import { AppointmentRequest } from '../../../types/appointmentRequests';
import { AcademiaResponse } from '../../../types/common';
import {
  AssociateReferral,
  Referral,
  ReferralReqBody,
} from '../../../types/referral';
import { returnQueryConfig } from '../../../utils/commonFunctions';

const useReferralActions = (
  whichAction: 'referralList' | 'referralsById' | 'referredById' | '',
  associateId?: number,
  clientId?: number
) => {
  // query function to fetch all referrals from database declared here
  const getReferralsListHandler = useQuery({
    queryKey: ['get-referrals-list'],
    queryFn: async (): Promise<AcademiaResponse<Array<Referral>>> => {
      const response = await getPrivateAxiosInstance('user').get(
        '/referral/list/'
      );

      return response.data;
    },

    ...returnQueryConfig(whichAction === 'referralList'),
  });

  // query function to fetch all referrals by associate id declared here
  const getReferralsByAssociateId = useQuery({
    queryKey: ['get-referrals-by-id', associateId],
    queryFn: async (): Promise<AcademiaResponse<Array<AssociateReferral>>> => {
      const response = await getPrivateAxiosInstance('user').get(
        `/associate/referrals/${associateId}/`
      );

      return response.data;
    },

    ...returnQueryConfig(whichAction === 'referralsById' && !!associateId),
  });

  // query function to fetch all referred to referrals by associate id declared here
  const getReferredByAssociateId = useQuery({
    queryKey: ['get-referred-by-id', associateId],
    queryFn: async (): Promise<AcademiaResponse<Array<AssociateReferral>>> => {
      const response = await getPrivateAxiosInstance('user').get(
        `/associate/referred/${associateId}/`
      );

      return response.data;
    },

    ...returnQueryConfig(whichAction === 'referredById' && !!associateId),
  });

  const getClientReferralsByClientId = useQuery({
    queryKey: ['get-client-referrals', clientId],
    queryFn: async (): Promise<AcademiaResponse<Array<AppointmentRequest>>> => {
      const response = await getPrivateAxiosInstance('user').get(
        `/client/referred/data/${clientId}/`
      );

      return response.data;
    },

    ...returnQueryConfig(!!clientId),
  });

  // mutation function to create new referral declared here
  const createNewReferralHandler = useMutation({
    mutationFn: async ({
      body,
    }: {
      body: ReferralReqBody;
    }): Promise<AcademiaResponse<Referral>> => {
      const response = await getPrivateAxiosInstance('user').post(
        '/referral/create/',
        body
      );

      return response.data;
    },
  });

  return {
    getReferralsListHandler,
    getReferralsByAssociateId,
    getReferredByAssociateId,
    getClientReferralsByClientId,
    createNewReferralHandler,
  };
};

export default useReferralActions;
